import {
  CommonTrackingData,
  getCommonTrackingData,
  trackingModelIdSplitter,
  VwTrack,
  VwTrackingEvents,
} from '@volkswagen-onehub/vw-e-mobility-feature-app-commons';
import {
  TrackingEvent,
  TrackingFunctions,
} from '@volkswagen-onehub/whitelabel-e-mobility-commons';
import {CostSimulatorTrackingCtx} from '@volkswagen-onehub/whitelabel-e-mobility-cost-feature-app';

const getCommonData = ({
  tenantConfiguration,
  selectedModel,
  selectedTrim,
}: CostSimulatorTrackingCtx): CommonTrackingData => {
  const [trackingModelId, trackingModelTrimFeatures] = trackingModelIdSplitter(
    selectedModel.id
  );
  return getCommonTrackingData(!!tenantConfiguration.global?.commercial, {
    EquipmentlineName: selectedTrim.trimId,
    CarlineId: selectedModel.carlineId,
    CarlineName: selectedModel.carlineName,
    SalesgroupId: selectedModel.salesgroupId,
    SalesgroupName: selectedModel.salesgroupName,
    ModelId: trackingModelId,
    ModelTrimFeatures: trackingModelTrimFeatures,
    ModelName: selectedModel.name,
    EnginePowerHPKW: selectedModel.engine,
  });
};

export function getCostTrackingFunctions(
  track: VwTrack
): TrackingFunctions<CostSimulatorTrackingCtx> {
  return {
    [TrackingEvent.FeatureAppLoaded]: (_, ctx) =>
      track(
        VwTrackingEvents.VWBasic_eMobilityTools_Load,
        getCommonData(ctx),
        undefined
      ),
    [TrackingEvent.EducationalInfoClick]: ({label, link}, ctx) => {
      track(VwTrackingEvents.VWBasic_VehicleLink_Click, getCommonData(ctx), {
        eventInfo: {
          contentId: label,
        },
        link,
      });
    },
    [TrackingEvent.ModelEfficiencyDataLayerOpen]: ({label, link}, ctx) => {
      track(VwTrackingEvents.VWBasic_VehicleLink_Click, getCommonData(ctx), {
        eventInfo: {
          contentId: label,
        },
        link,
      });
    },
    [TrackingEvent.BasicButtonClick]: ({label, link}, ctx) =>
      track(VwTrackingEvents.VWBasic_Button_Click, getCommonData(ctx), {
        eventInfo: {
          contentId: label,
        },
        link,
      }),
    [TrackingEvent.DrivingProfileChange]: ({locationPercentages}, ctx) =>
      track(VwTrackingEvents.VWBasic_FilterSelect_Click, getCommonData(ctx), {
        filter: {
          type: 'multi_value',
          values: locationPercentages,
          name: 'driving-profile',
        },
      }),
    [TrackingEvent.AnnualDistanceChange]: ({distanceKm}, ctx) =>
      track(VwTrackingEvents.VWBasic_FilterSelect_Click, getCommonData(ctx), {
        filter: {
          type: 'single_value',
          values: [distanceKm],
          name: 'annual-distance',
        },
      }),
    [TrackingEvent.ChargingLocationChange]: ({location}, ctx) =>
      track(VwTrackingEvents.VWBasic_FilterSelect_Click, getCommonData(ctx), {
        filter: {
          type: 'single_value',
          values: [location],
          name: 'charging-location-select',
        },
      }),
    [TrackingEvent.ChargingLocationRatioChange]: ({location}, ctx) =>
      track(VwTrackingEvents.VWBasic_FilterSelect_Click, getCommonData(ctx), {
        filter: {
          type: 'multi_value',
          values: Object.values(location),
          name: 'charging-location-ratio-change',
        },
      }),
    [TrackingEvent.ChargingLocationRatioResetButtonClick]: ({location}, ctx) =>
      track(VwTrackingEvents.VWBasic_FilterReset_Click, getCommonData(ctx), {
        filter: {
          type: 'multi_value',
          values: Object.values(location),
          name: 'charging-location-ratio-reset-button-click',
        },
      }),
    [TrackingEvent.CarbonOffsetChange]: ({value, name}, ctx) =>
      track(VwTrackingEvents.VWBasic_Checkbox_Click, getCommonData(ctx), {
        eventInfo: {
          contentId: value,
        },
        link: {
          name,
        },
      }),
    [TrackingEvent.WeChargeTariffChange]: ({value, name}, ctx) =>
      track(VwTrackingEvents.VWBasic_Checkbox_Click, getCommonData(ctx), {
        eventInfo: {
          contentId: value,
        },
        link: {
          name,
        },
      }),
    [TrackingEvent.CostTrimSelect]: ({trim, model}, ctx) => {
      const [trackingModelId, trackingModelTrimFeatures] =
        trackingModelIdSplitter(model.id);
      track(VwTrackingEvents.VWBasic_VehicleSelect_Click, getCommonData(ctx), {
        configuration: {
          // We need to overwrite all this from the common data with the new
          // values.
          EquipmentlineName: trim.trimId,
          CarlineId: model.carlineId,
          CarlineName: model.carlineName,
          SalesgroupId: model.salesgroupId,
          SalesgroupName: model.salesgroupName,
          ModelId: trackingModelId,
          ModelTrimFeatures: trackingModelTrimFeatures,
          ModelName: model.name,
          EnginePowerHPKW: model.engine,
        },
      });
    },
    [TrackingEvent.CostEngineSelect]: ({model}, ctx) => {
      const [trackingModelId, trackingModelTrimFeatures] =
        trackingModelIdSplitter(model.id);

      track(VwTrackingEvents.VWBasic_EngineSelect_Click, getCommonData(ctx), {
        configuration: {
          // We need to overwrite all this from the common (old) data with the new
          // values.
          ModelId: trackingModelId,
          ModelTrimFeatures: trackingModelTrimFeatures,
          ModelName: model.name,
          EnginePowerHPKW: model.engine,
        },
      });
    },
  };
}
